import React, { FC, useEffect, useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useLazyQuery, useMutation } from '@apollo/client';
import { getLabTemplate, saveTransportSettingsTemplate } from 'apollo/templates/organization';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import Loader from 'components/shared/loader/loader.component';
import { TransportPartner, TransportSettings } from 'models/organization';
import { LoadingButton } from '@mui/lab';
import NShiftTransportSettings from './nshift-transport-settings/nshift-transport-settings.component';
import DenthubTransportSettings from './denthub-transport-settings/denthub-transport-settings.component';

type TransportSettingsDialogProps = {
    dialogOpen: boolean;
    onCloseDialog: () => void;
    labId: string;
    onSave: () => void;
};
const TransportSettingsDialog: FC<TransportSettingsDialogProps> = ({
    dialogOpen,
    onCloseDialog,
    labId,
    onSave,
}: TransportSettingsDialogProps) => {
    const { t: translate } = useTranslation(['common', 'transport']);
    const { showBoundary } = useDenthubErrorBoundary();
    const [transportSettings, setTransportSettings] = useState<TransportSettings>({
        denthubTransport: { isEnabled: false },
        nShiftTransport: { isEnabled: false },
    } as TransportSettings);

    const [saveTransportSettings, { loading: saveLoading }] = useMutation(saveTransportSettingsTemplate, {
        onError: (err: Error) => {
            showBoundary(err);
        },
        onCompleted: () => {
            onSave();
        },
    });

    const [getLab, { loading }] = useLazyQuery(getLabTemplate, {
        onError: (err: Error) => {
            showBoundary(err);
        },
        onCompleted: ({ lab }) => {
            setTransportSettings(
                lab.transportSettings || {
                    denthubTransport: { isEnabled: false },
                    nShiftTransport: { isEnabled: false },
                },
            );
        },
    });

    useEffect(() => {
        getLab({ variables: { id: labId } });
    }, [labId]);

    const saveSettings = () => {
        saveTransportSettings({
            variables: {
                labId,
                transportSettings,
            },
        });
    };

    const handlePartnersChange = (partners: TransportPartner[]) => {
        const settings = { ...transportSettings };
        settings.nShiftTransport = { ...settings.nShiftTransport, partners };
        setTransportSettings(settings);
    };

    const handleDenthubTransportToggle = (isEnabled: boolean) => {
        const settings = { ...transportSettings };
        settings.denthubTransport = { ...settings.denthubTransport, isEnabled };
        setTransportSettings(settings);
    };

    const handleExternalIdChange = (externalId: string) => {
        const settings = { ...transportSettings };
        settings.denthubTransport = { ...settings.denthubTransport, externalId };
        setTransportSettings(settings);
    };

    const handleNShiftTransportToggle = (isEnabled: boolean) => {
        const settings = { ...transportSettings };
        settings.nShiftTransport = { ...settings.nShiftTransport, isEnabled };
        setTransportSettings(settings);
    };

    if (loading) {
        return <Loader />;
    }

    return (
        <Dialog fullWidth maxWidth="xl" open={dialogOpen} onClose={onCloseDialog}>
            <DialogTitle>{translate('transport:TRANSPORT_SETTINGS')}</DialogTitle>
            <DialogContent>
                <DenthubTransportSettings
                    denthubTransport={transportSettings?.denthubTransport}
                    onToggleTransport={handleDenthubTransportToggle}
                    onExternalIdChange={handleExternalIdChange}
                />
                <NShiftTransportSettings
                    nShiftTransport={transportSettings?.nShiftTransport}
                    onPartnersChange={handlePartnersChange}
                    onToggleTransport={handleNShiftTransportToggle}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onCloseDialog}>{translate('common:CANCEL')}</Button>
                <LoadingButton loading={saveLoading} variant="contained" type="submit" onClick={saveSettings}>
                    {translate('common:SAVE')}
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
};

export default TransportSettingsDialog;
